import React, { useRef } from 'react';
import { T } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { SpinnerIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { useSd844TemplateEditor } from 'src/api/rpc/hooks/ship-and-debit/useSd844TemplateEditor';
import { Sd844TemplateForm } from './Sd844TemplateForm';
import { Sd844BuiltInDefaultPreview } from './Sd844BuiltInDefaultPreview';

/**
 * Upload and bind a supplier's own 844 claim form.
 *
 * Where a template exists, the supplier's email claim arrives as THEIR form,
 * populated, instead of our default 34-column workbook. The uploaded file is
 * authoritative — we populate cells and change nothing else.
 *
 * Display only: the state and mutations live in `useSd844TemplateEditor`.
 */

interface Props {
  /** `null` edits the organization default template. */
  companyId: string | null;
  /** Only rendered for email-delivery suppliers; EDI claims carry no attachment. */
  enabled: boolean;
  /** False shows the template read-only (an S&D viewer). */
  canEdit?: boolean;
}

export function Sd844EmailTemplateEditor({
  companyId,
  enabled,
  canEdit = true,
}: Props) {
  const editor = useSd844TemplateEditor(companyId, enabled, canEdit);
  const { template, pendingUpload, form } = editor;

  if (!enabled) return null;

  const hasFile = !!template || !!pendingUpload;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Label className="text-xs">
            <T context="section heading for a supplier's own 844 claim form">
              844 Email Template
            </T>
          </Label>
          <p className="text-muted-foreground text-xs">
            <T context="explains that an uploaded supplier form replaces the default 844 attachment">
              Upload the supplier&apos;s own claim form and we send that,
              populated, instead of the default workbook. Put the
              supplier&apos;s headers in row 1; in row 2 you can note where each
              column comes from (e.g. &quot;SPA Quote No (Column G)&quot;, or a
              fixed value like &quot;CAD&quot;) and highlight columns to leave
              blank in yellow — we read those notes to pre-fill the mapping, and
              row 2 is replaced by claim lines when sent.
            </T>
          </p>
        </div>
        {canEdit && (
          <UploadTemplateButton
            hasTemplate={!!template}
            isParsing={editor.isParsing}
            isBusy={editor.isBusy}
            onPick={editor.pickFile}
          />
        )}
      </div>

      {editor.isLoading && (
        <p className="text-muted-foreground text-xs">
          <T context="loading state for the 844 template section">Loading…</T>
        </p>
      )}

      {!editor.isLoading && !hasFile && companyId !== null && (
        <p className="text-muted-foreground text-xs">
          <T context="shown when a supplier has no custom 844 template">
            No template. This supplier receives the default 844 template.
          </T>
        </p>
      )}

      {/* The org default with nothing saved yet: show the built-in one. */}
      {!editor.isLoading && !hasFile && companyId === null && (
        <Sd844BuiltInDefaultPreview canEdit={canEdit} />
      )}

      {form && hasFile && <Sd844TemplateForm form={form} editor={editor} />}
    </div>
  );
}

/** The hidden file input and the button that opens it. */
function UploadTemplateButton({
  hasTemplate,
  isParsing,
  isBusy,
  onPick,
}: {
  hasTemplate: boolean;
  isParsing: boolean;
  /** Blocks picking a file while any other write is in flight. */
  isBusy: boolean;
  onPick: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so re-picking the same file still fires a change event.
          e.target.value = '';
          if (file) onPick(file);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={isBusy}
      >
        {isParsing ? (
          <SpinnerIcon className="size-4 animate-spin" />
        ) : (
          <UploadSimpleIcon className="size-4" />
        )}
        {hasTemplate ? (
          <T context="button to replace an existing 844 template file">
            Replace File
          </T>
        ) : (
          <T context="button to upload a supplier 844 template file">
            Upload Template
          </T>
        )}
      </Button>
    </>
  );
}
